import * as React from 'react';
import {createViewConfig, view} from '@sail/engine';
import type {View} from '../view';
import {css} from '../css';

const TRANSITION_TIMING = '200ms';

const TRANSITION = [
  `transform ${TRANSITION_TIMING} ease-in-out`,
  `opacity ${TRANSITION_TIMING} ease-in-out`,
  `grid-template-rows ${TRANSITION_TIMING} ease-in-out`,
].join(', ');

function isEmptyValue(value: string): boolean {
  return !value || value === '0' || value === '0px';
}

export type CollapsibleContainerProps = View.IntrinsicElement<
  'section',
  {
    /**
     * Minimum height to which the panel should collapse. Used for truncating long content.
     */
    minHeight: string;
    /**
     * Spread into the component from collapsibleProps returned from useCollapsible
     */
    id: string;
    /**
     * Open state of Collapsible area. Spread into the component from collapsibleProps returned from useCollapsible
     */
    open: boolean;
    /**
     * Contents of the collapsible container.
     */
    children: React.ReactNode;
  }
>;

export const CollapsibleContainerConfig = createViewConfig({
  props: {} as CollapsibleContainerProps,
  name: 'CollapsibleContainer',
  defaults: {
    minHeight: '0',
  },
  flattens: true,
});

export const CollapsibleContainer = CollapsibleContainerConfig.createView(
  ({minHeight, open, id, children, ...props}) => {
    const ref = React.useRef<HTMLDivElement>(null);

    const uses = React.useMemo(() => {
      const hasMinHeight = !isEmptyValue(minHeight);
      const visibleContent = open || hasMinHeight;

      return [
        css({display: 'grid'}),
        open ? css({gridTemplateRows: '1fr'}) : css({gridTemplateRows: '0fr'}),
        css({paddingBottom: minHeight}),
        visibleContent ? css({opacity: '1'}) : css({opacity: '0'}),
        css({overflow: 'hidden', position: 'relative', scrollbarWidth: 'none'}),
        visibleContent
          ? css({transform: 'translateY(0) scale(1)'})
          : css({transform: 'translateY(-2px) scale(0.99)'}),
        open
          ? css({
              transition: `${TRANSITION}, visibility 0ms linear, overflow ${TRANSITION_TIMING} step-end allow-discrete`,
            })
          : css({
              transition: `${TRANSITION}, visibility 0ms linear ${TRANSITION_TIMING}, overflow ${TRANSITION_TIMING} step-start allow-discrete`,
            }),
        visibleContent
          ? css({visibility: 'visible'})
          : css({visibility: 'hidden'}),
        css({
          '@media (prefers-reduced-motion)': {
            transitionDuration: 'instant',
          },
        }),
        hasMinHeight && !open
          ? css({
              '::after': {
                content: "''",
                position: 'absolute',
                bottom: '-40px',
                width: 'fill',
                height: 'space.500',
                boxShadow: 'large',
              },
            })
          : css({}),
      ];
    }, [open, minHeight]);

    return (
      <view.section role="region" id={id} ref={ref} uses={uses} {...props}>
        <view.div
          css={{
            marginBottom: `-${minHeight}`,
            minHeight: 'space.0',
            minWidth: 'space.0',
          }}
        >
          {children}
        </view.div>
      </view.section>
    );
  },
);
